import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface AdminStats {
  totalUsers: number;
  activeStudents: number;
  totalOrders: number;
  paidOrders: number;
  totalRevenue: number;
  totalCourses: number;
  publishedCourses: number;
  recentOrders: Array<{
    id: string;
    merchantOid: string;
    status: string;
    totalAmount: number;
    createdAt: string;
    user: { firstName: string; lastName: string; email: string };
  }>;
  recentUsers: Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    createdAt: string;
  }>;
}

export interface AdminCourse {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string;
  thumbnailUrl: string | null;
  price: number;
  oldPrice: number | null;
  isPublished: boolean;
  isFeatured: boolean;
  publishedAt: string | null;
  categoryId: string | null;
  category: { name: string; slug: string } | null;
  _count: { sections: number; enrollments: number };
  createdAt: string;
}

export interface AdminPackage {
  id: string;
  slug: string;
  name: string;
  description: string;
  shortDescription: string | null;
  price: number;
  oldPrice: number | null;
  isPopular: boolean;
  isPublished: boolean;
  includesLive: boolean;
  featuresJson: string[];
  accessDays: number | null;
  sortOrder: number;
  courses: Array<{ course: { id: string; slug: string; title: string } }>;
  _count: { enrollments: number };
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: "STUDENT" | "INSTRUCTOR" | "ADMIN";
  emailVerifiedAt: string | null;
  createdAt: string;
  _count: { enrollments: number; orders: number };
}

export interface AdminOrder {
  id: string;
  merchantOid: string;
  status: "PENDING" | "PAID" | "CANCELLED" | "REFUNDED" | "EXPIRED";
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  currency: string;
  billingFirstName: string;
  billingLastName: string;
  billingEmail: string;
  billingPhone: string;
  billingAddress: string;
  billingCity: string;
  billingDistrict: string | null;
  billingPostalCode: string | null;
  createdAt: string;
  paidAt: string | null;
  shippedAt: string | null;
  shippingStatus: "AWAITING" | "SHIPPED" | "CANCELLED";
  kargonomiShipmentId: number | null;
  shippingTrackingCode: string | null;
  shippingBarcode: string | null;
  shippingProviderName: string | null;
  kargonomiCancelledAt: string | null;
  kargonomiCancelledShipmentId: number | null;
  shippingCancelledProviderName: string | null;
  user: { email: string; firstName: string; lastName: string };
  items: Array<{
    id: string;
    itemName: string;
    unitPrice: number;
    quantity: number;
    subtotal: number;
    bookId: string | null;
    bookFormat: "DIGITAL" | "PHYSICAL" | null;
  }>;
}

export interface AdminAnnouncement {
  id: string;
  title: string;
  body: string;
  level: "INFO" | "WARNING" | "SUCCESS";
  startAt: string;
  endAt: string | null;
  isActive: boolean;
  showOnHome: boolean;
  showInPanel: boolean;
}

export const useAdminLockdownStatus = () =>
  useQuery({
    queryKey: ["admin", "lockdown-status"],
    queryFn: () =>
      api.get<{ active: boolean; mode: "off" | "write" | "full" }>(
        "/api/admin/lockdown-status",
        { auth: true },
      ),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

export const useAdminStats = () =>
  useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => api.get<AdminStats>("/api/admin/stats/overview", { auth: true }),
  });

export const useAdminCourses = () =>
  useQuery({
    queryKey: ["admin", "courses"],
    queryFn: () => api.get<AdminCourse[]>("/api/admin/courses", { auth: true }),
  });

export const useAdminCourse = (id: string | undefined) =>
  useQuery({
    queryKey: ["admin", "course", id],
    queryFn: () => api.get(`/api/admin/courses/${id}`, { auth: true }),
    enabled: !!id,
  });

export const useCreateCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<AdminCourse>("/api/admin/courses", input, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "courses"] }),
  });
};

export const useUpdateCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Record<string, unknown>) =>
      api.patch(`/api/admin/courses/${id}`, input, { auth: true }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "courses"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "course", vars.id] });
    },
  });
};

export const useDeleteCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/admin/courses/${id}`, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "courses"] }),
  });
};

export const useAdminPackages = () =>
  useQuery({
    queryKey: ["admin", "packages"],
    queryFn: () => api.get<AdminPackage[]>("/api/admin/packages", { auth: true }),
  });

export const useUpsertPackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<AdminPackage>("/api/admin/packages", input, { auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "packages"] });
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
  });
};

export const useDeletePackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/admin/packages/${id}`, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "packages"] }),
  });
};

export const useAdminUsers = (params?: {
  q?: string;
  role?: string;
  packageId?: string;
  courseId?: string;
}) =>
  useQuery({
    queryKey: ["admin", "users", params],
    queryFn: () => api.get<AdminUser[]>("/api/admin/users", { auth: true, query: params }),
  });

export const useGrantAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { userId: string; courseId?: string; packageId?: string }) =>
      api.post(`/api/admin/users/${vars.userId}/grant-access`, vars, { auth: true }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "user-enrollments", vars.userId] });
    },
  });
};

export interface UserEnrollment {
  id: string;
  source: "ORDER" | "MANUAL" | "IMPORT";
  accessUntil: string | null;
  isActive: boolean;
  createdAt: string;
  orderId: string | null;
  course: { id: string; slug: string; title: string } | null;
  package: { id: string; slug: string; name: string } | null;
  order: { merchantOid: string; status: string } | null;
}

export const useUserEnrollments = (userId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ["admin", "user-enrollments", userId],
    queryFn: () => api.get<UserEnrollment[]>(`/api/admin/users/${userId}/enrollments`, { auth: true }),
    enabled: enabled && !!userId,
  });

export const useRevokeAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { enrollmentId: string; userId: string }) =>
      api.del(`/api/admin/enrollments/${vars.enrollmentId}`, { auth: true }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "user-enrollments", vars.userId] });
    },
  });
};

export interface BulkRevokeAccessResult {
  deactivated: number;
  deleted: number;
  total: number;
}

export const useBulkRevokeAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { packageId?: string; courseId?: string; userIds?: string[] }) =>
      api.post<BulkRevokeAccessResult>("/api/admin/enrollments/bulk-revoke", vars, { auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "user-enrollments"] });
    },
  });
};

export const useAdminOrders = (status?: string, opts?: { physicalOnly?: boolean }) =>
  useQuery({
    queryKey: ["admin", "orders", status, opts?.physicalOnly ? "physical" : "all"],
    queryFn: () =>
      api.get<AdminOrder[]>("/api/admin/orders", {
        auth: true,
        query: {
          status,
          physicalOnly: opts?.physicalOnly ? "1" : undefined,
        },
      }),
  });

export const useRefundOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/orders/${id}/refund`, undefined, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "orders"] }),
  });
};

export const useSetOrdersShipped = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { orderIds: string[]; shipped: boolean }) =>
      api.post<{ updated: number }>("/api/admin/orders/shipping-status", vars, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "orders"] }),
  });
};

export interface KargonomiShipResult {
  orderId: string;
  merchantOid: string;
  kargonomiShipmentId: number | null;
  shippingTrackingCode: string | null;
  shippingBarcode: string | null;
  shippingProviderName: string | null;
  shippedAt: string | null;
  matchedLocation: { state: string; city: string };
}

export const useShipOrderViaKargonomi = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      api.post<KargonomiShipResult>(`/api/admin/orders/${orderId}/kargonomi-ship`, undefined, {
        auth: true,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "orders"] }),
  });
};

export const useShipOrdersViaKargonomiBulk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderIds: string[]) =>
      api.post<{
        total: number;
        success: number;
        failed: number;
        results: Array<{ orderId: string; ok: boolean; error?: string; merchantOid?: string }>;
      }>("/api/admin/orders/kargonomi-ship-bulk", { orderIds }, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "orders"] }),
  });
};

export const useRefreshKargonomiShipment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      api.post<{
        orderId: string;
        merchantOid: string;
        shippingTrackingCode: string | null;
        shippingBarcode: string | null;
        shippingProviderName: string | null;
        kargonomiStatus: string | null;
        kargonomiStatusLabel: string | null;
      }>(`/api/admin/orders/${orderId}/kargonomi-refresh`, undefined, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "orders"] }),
  });
};

export const useCancelKargonomiShipment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      api.post<{
        orderId: string;
        merchantOid: string;
        message: string;
        status: "cancelled";
        kargonomiCancelledAt: string | null;
        kargonomiCancelledShipmentId: number | null;
        shippingCancelledProviderName: string | null;
      }>(`/api/admin/orders/${orderId}/kargonomi-cancel`, undefined, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "orders"] }),
  });
};

export const fetchKargonomiLabel = (orderId: string) =>
  api.get<{
    orderId: string;
    merchantOid: string;
    format: "pdf";
    contentType: string;
    base64: string;
    filename: string;
  }>(`/api/admin/orders/${orderId}/kargonomi-label`, { auth: true });

export const useAdminAnnouncements = () =>
  useQuery({
    queryKey: ["admin", "announcements"],
    queryFn: () => api.get<AdminAnnouncement[]>("/api/admin/announcements", { auth: true }),
  });

export const useUpsertAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post("/api/admin/announcements", input, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "announcements"] }),
  });
};

export const useDeleteAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/admin/announcements/${id}`, { auth: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "announcements"] }),
  });
};
